// React
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// Utils
import { COMPAIGNS_ACTION_TYPE, HAS_ENVELOPE } from '~/utils/constants';

// MUI Components
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';

import DataGrid from '~/components/General/OlcDataGrid';
import { GreenTickCIcon, MailerProofReviewCIcon } from '~/assets/images';

// Styles
import './styles.scss';

const TABLE_HEADER_HEIGHT = 56;
const TABLE_ROW_HEIGHT = 56;
const GRID_BORDER_ALLOWANCE = 2;
const SCROLL_ACTIVATION_TOLERANCE = 8;
const REVIEW_STACK_BREAKPOINT = 1100;
const REVIEW_TABLE_BOTTOM_GUTTER = 48;

const OrderedProducts = (props) => {
  const { selectedCampaign, downloadViewProof, downloadEnvelopeProof, downloadingRows } = props;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const tableFrameRef = useRef(null);
  const [scrollFrameHeight, setScrollFrameHeight] = useState(null);

  const rowHasEnvelopeProof = (row) => {
    if (!row) return false;
    return (
      HAS_ENVELOPE.includes(+(row?.productId || 0)) ||
      !!row?.selectedEnvelope ||
      !!row?.envelopeThumbnailUrl ||
      !!row?.envelopeType ||
      !!row?.isEnvelopeDownloaded
    );
  };

  const renderProofDownloadAction = ({
    label,
    loading: proofLoading,
    downloaded,
    onClick,
    trailingSpacing = false,
  }) => {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', flex: '0 0 auto', gap: 1 }}>
        {proofLoading ? (
          <CircularProgress size={20} sx={{ color: '#ed5c2f' }} />
        ) : (
          <img
            onClick={onClick}
            src={MailerProofReviewCIcon}
            alt="Download"
            style={{ width: '20px', height: '20px' }}
          />
        )}
        <Typography onClick={onClick} className="downloadProofLink">
          {label}
        </Typography>
        <Box
          sx={{
            width: '20px',
            height: '20px',
            marginRight: trailingSpacing ? '18px' : 0,
          }}
        >
          {downloaded && <img src={GreenTickCIcon} alt="green" />}
        </Box>
      </Box>
    );
  };

  useEffect(() => {
    if (selectedCampaign?.templates) {
      const computedRows = selectedCampaign?.templates.map(
        (
          {
            label,
            frontThumbnailUrl,
            backThumbnailUrl,
            productType,
            productId,
            isDownloaded,
            isEnvelopeDownloaded,
            selectedEnvelope,
            envelopeThumbnailUrl,
            envelopeType,
          },
          index
        ) => ({
          id: index,
          label: label,
          productType: productType,
          frontThumbnailUrl: frontThumbnailUrl,
          backThumbnailUrl: backThumbnailUrl,
          productId,
          isDownloaded,
          isEnvelopeDownloaded,
          selectedEnvelope,
          envelopeThumbnailUrl,
          envelopeType,
        })
      );
      setRows(computedRows);
      const loadingTimer = window.setTimeout(() => setLoading(false), 300);

      return () => window.clearTimeout(loadingTimer);
    } else {
      setRows([]);
      setLoading(false);
    }
  }, [JSON.stringify(selectedCampaign?.templates)]);

  useLayoutEffect(() => {
    if (selectedCampaign?.templates) {
      setLoading(true);
    }
  }, [selectedCampaign?.templates]);

  const LoadingSkeleton = () => (
    <Box className="skeletonContainer">
      {[1, 2, 3].map((item) => (
        <Box key={item} className="skeletonRow" sx={{ gap: 2 }}>
          <Skeleton variant="text" width="48%" height={24} />
          <Skeleton variant="text" width="20%" height={24} />
          <Skeleton variant="text" width="26%" height={24} />
        </Box>
      ))}
    </Box>
  );

  const gridContentHeight = useMemo(() => {
    return TABLE_HEADER_HEIGHT + rows.length * TABLE_ROW_HEIGHT + GRID_BORDER_ALLOWANCE;
  }, [rows.length]);

  useLayoutEffect(() => {
    if (loading || rows.length === 0) {
      setScrollFrameHeight(null);
      return undefined;
    }

    let frameId;
    let resizeObserver;

    const measureTable = () => {
      const frame = tableFrameRef.current;
      const reviewStep = frame?.closest('.reviewOrderStep');
      const flowContent = frame?.closest('.orderFlowContent');

      if (!frame || !reviewStep || !flowContent || window.innerWidth <= REVIEW_STACK_BREAKPOINT) {
        setScrollFrameHeight(null);
        return;
      }

      const frameRect = frame.getBoundingClientRect();
      const flowContentRect = flowContent.getBoundingClientRect();
      const reviewStepStyles = window.getComputedStyle(reviewStep);
      const reviewStepPaddingBottom = Number.parseFloat(reviewStepStyles.paddingBottom) || 0;
      const availableHeight = Math.floor(
        flowContentRect.bottom -
          frameRect.top -
          reviewStepPaddingBottom -
          REVIEW_TABLE_BOTTOM_GUTTER
      );
      const nextScrollHeight =
        availableHeight > 0 && gridContentHeight > availableHeight + SCROLL_ACTIVATION_TOLERANCE
          ? availableHeight
          : null;

      setScrollFrameHeight((currentHeight) =>
        currentHeight === nextScrollHeight ? currentHeight : nextScrollHeight
      );
    };

    const scheduleMeasure = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(() => {
        frameId = undefined;
        measureTable();
      });
    };

    measureTable();
    scheduleMeasure();

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleMeasure);

      if (tableFrameRef.current) {
        resizeObserver.observe(tableFrameRef.current);
        const flowContent = tableFrameRef.current.closest('.orderFlowContent');
        if (flowContent) resizeObserver.observe(flowContent);
      }
    }

    window.addEventListener('resize', scheduleMeasure);

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
    };
  }, [loading, rows.length, gridContentHeight]);

  const columns = [
    {
      field: 'templateName',
      headerName: 'Template',
      flex: 1.2,
      minWidth: 180,
      compactShrinkMinWidth: 140,
      compactShrinkPriority: 0,
      compactValueGetter: (row) => row?.label,
      headerAlign: 'left',
      align: 'left',
      sortable: false,
      renderCell: (params) => {
        return (
          <Tooltip title={params.row.label} placement="top-start">
            <Typography className="templateNameCell">{params.row.label || '-'}</Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'productType',
      headerName: 'Mailer',
      flex: 1.2,
      minWidth: 180,
      compactShrinkMinWidth: 90,
      compactShrinkPriority: 1,
      headerAlign: 'left',
      align: 'left',
      sortable: false,
      renderCell: (params) => {
        return (
          <Tooltip
            title={
              COMPAIGNS_ACTION_TYPE.find(
                (product) => +product?.productId === +params?.row?.productId
              )?.label || params?.row?.productType
            }
            placement="top-start"
          >
            <Typography className="templateNameCell">
              {COMPAIGNS_ACTION_TYPE.find(
                (product) => +product?.productId === +params?.row?.productId
              )?.label || params?.row?.productType}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'frontThumbnailUrl',
      headerName: 'Proofs',
      width: 320,
      minWidth: 320,
      compactFill: false,
      compactShrink: false,
      compactShrinkPriority: 3,
      compactValueGetter: (row) => (rowHasEnvelopeProof(row) ? 'Mailer Envelope' : 'Mailer'),
      sortable: false,
      renderCell: (params) => {
        const mailerIsLoading = downloadingRows?.some(
          (row) => row.index === params.row.id && row.type === 'mailer'
        );
        const envelopeIsLoading = downloadingRows?.some(
          (row) => row.index === params.row.id && row.type === 'envelope'
        );
        const hasEnvelopeProof = rowHasEnvelopeProof(params.row);

        return (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexWrap: 'nowrap',
              minWidth: 0,
              whiteSpace: 'nowrap',
            }}
          >
            {renderProofDownloadAction({
              label: 'Mailer',
              loading: mailerIsLoading,
              downloaded: params.row.isDownloaded,
              onClick: () => downloadViewProof(params.row.id),
              trailingSpacing: hasEnvelopeProof,
            })}
            {hasEnvelopeProof &&
              renderProofDownloadAction({
                label: 'Envelope',
                loading: envelopeIsLoading,
                downloaded: params.row.isEnvelopeDownloaded,
                onClick: () => downloadEnvelopeProof(params.row.id),
              })}
          </Box>
        );
      },
    },
  ];

  if (loading) {
    return (
      <Box className="campaignsMailDateContainer">
        <Paper className="tableContainer" ref={tableFrameRef}>
          <LoadingSkeleton />
        </Paper>
      </Box>
    );
  }

  return (
    <Box className="thumnailTableContainer">
      <Paper className="tableContainer" ref={tableFrameRef}>
        <Box className="dataGridWrapper">
          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight={!scrollFrameHeight}
            shrinkColumnsToFit
            getRowId={(row) => row?.id}
            hideFooter
            hideFooterPagination
            hideFooterSelectedRowCount
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            disableRowSelectionOnClick
            className={`mailDateTable ${
              scrollFrameHeight ? 'mailDateTable--scrollFrame' : ''
            }`.trim()}
            rowHeight={TABLE_ROW_HEIGHT}
            columnHeaderHeight={TABLE_HEADER_HEIGHT}
            getRowHeight={() => TABLE_ROW_HEIGHT}
            sx={{
              ...(scrollFrameHeight ? { height: `${scrollFrameHeight}px` } : {}),
              '& .MuiDataGrid-root': {
                border: 'none',
              },
              '& .MuiDataGrid-main': {
                borderRadius: 'inherit',
                overflow: 'hidden',
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default OrderedProducts;
